import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Input } from "./ui/input";
import { Loader2, MapPin, Search } from "lucide-react";

// Default centre: Castrovillari (EasyIce HQ)
const DEFAULT_CENTER = [39.8122, 16.2001];
const DEFAULT_ZOOM = 15;

// Fix Leaflet default marker icons (webpack issue)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

/**
 * DeliveryAddressPicker
 * Props:
 *  - value: { address: string, lat: number|null, lng: number|null }
 *  - onChange: (v) => void
 *  - placeholder?: string
 */
const DeliveryAddressPicker = ({ value, onChange, placeholder = "Cerca via, numero civico, città..." }) => {
  const [query, setQuery] = useState(value?.address || "");
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [showSug, setShowSug] = useState(false);
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markerRef = useRef(null);
  const debounceTimer = useRef(null);

  // Init map once
  useEffect(() => {
    if (mapInstance.current || !mapRef.current) return;
    const center = (value?.lat && value?.lng) ? [value.lat, value.lng] : DEFAULT_CENTER;
    const zoom = (value?.lat && value?.lng) ? 17 : DEFAULT_ZOOM;
    const m = L.map(mapRef.current).setView(center, zoom);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(m);

    const marker = L.marker(center, { draggable: true }).addTo(m);
    marker.on("dragend", async () => {
      const pos = marker.getLatLng();
      const addr = await reverseGeocode(pos.lat, pos.lng);
      const newVal = {
        address: addr || value?.address || "",
        lat: pos.lat,
        lng: pos.lng,
      };
      setQuery(newVal.address);
      onChange?.(newVal);
    });

    mapInstance.current = m;
    markerRef.current = marker;
    // force leaflet to recalc size after layout
    setTimeout(() => m.invalidateSize(), 200);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync marker when external value changes
  useEffect(() => {
    if (!mapInstance.current || !markerRef.current) return;
    if (value?.lat && value?.lng) {
      const latlng = [value.lat, value.lng];
      markerRef.current.setLatLng(latlng);
      mapInstance.current.setView(latlng, 17);
    }
    if (typeof value?.address === "string" && value.address !== query) {
      setQuery(value.address);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.lat, value?.lng, value?.address]);

  const reverseGeocode = async (lat, lng) => {
    try {
      const r = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=it&zoom=18`,
        { headers: { "Accept-Language": "it" } }
      );
      const data = await r.json();
      return data.display_name || "";
    } catch {
      return "";
    }
  };

  const searchAddress = async (q) => {
    if (!q || q.length < 3) {
      setSuggestions([]);
      return;
    }
    setLoadingSearch(true);
    try {
      const r = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&countrycodes=it&limit=6&accept-language=it&addressdetails=1`,
        { headers: { "Accept-Language": "it" } }
      );
      const data = await r.json();
      setSuggestions(data || []);
    } catch {
      setSuggestions([]);
    } finally {
      setLoadingSearch(false);
    }
  };

  const onQueryChange = (v) => {
    setQuery(v);
    setShowSug(true);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => searchAddress(v), 400);
  };

  const pickSuggestion = (s) => {
    const lat = parseFloat(s.lat);
    const lng = parseFloat(s.lon);
    const newVal = { address: s.display_name, lat, lng };
    setQuery(s.display_name);
    setShowSug(false);
    setSuggestions([]);
    onChange?.(newVal);
    if (mapInstance.current && markerRef.current) {
      markerRef.current.setLatLng([lat, lng]);
      mapInstance.current.setView([lat, lng], 17);
    }
  };

  return (
    <div className="space-y-3" data-testid="delivery-address-picker">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-sky-600 pointer-events-none" />
        <Input
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          onFocus={() => setShowSug(true)}
          onBlur={() => setTimeout(() => setShowSug(false), 200)}
          placeholder={placeholder}
          className="pl-9 h-11"
          data-testid="address-autocomplete-input"
        />
        {loadingSearch && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-sky-600 animate-spin" />
        )}
        {showSug && suggestions.length > 0 && (
          <div
            className="absolute z-30 left-0 right-0 mt-1 bg-white border border-sky-100 rounded-lg shadow-lg max-h-72 overflow-y-auto"
            data-testid="address-suggestions"
          >
            {suggestions.map((s, i) => (
              <button
                key={`${s.place_id}-${i}`}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); pickSuggestion(s); }}
                className="w-full text-left px-4 py-2.5 text-sm hover:bg-sky-50 flex items-start gap-2 border-b border-sky-50 last:border-0"
                data-testid={`address-suggestion-${i}`}
              >
                <MapPin className="w-4 h-4 text-sky-600 mt-0.5 shrink-0" />
                <span className="text-[#032B44]">{s.display_name}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <div
        ref={mapRef}
        className="w-full h-64 rounded-xl overflow-hidden border border-sky-100 z-0"
        data-testid="delivery-address-map"
      />
      <p className="text-[11px] text-slate-500 text-center">
        Cerca l'indirizzo oppure <strong>trascina il pin</strong> sulla mappa per precisione.
      </p>
    </div>
  );
};

export default DeliveryAddressPicker;
